import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPause, FaPlay } from 'react-icons/fa';
import Header from '../../Components/Header/Header';
import api from '../../api';
import { errorMessage, mediaUrl } from './docShared';
import './documentaries.css';
import './timeline.css';

// The timeline editor of a finished video. Everything you change here is a list of "changes" (edits) kept in the
// page until you press "Render changes": the timeline and the inspector show the result straight away, the player
// shows a real rendered frame of it (the engine's preview), and only the scenes you touched are rendered again.

// ── helpers ──────────────────────────────────────────────────────────────────────────────────────────────────

const TRACKS = [
  { key: 'scenes', name: 'Scenes' },
  { key: 'video', name: 'Footage and photos' },
  { key: 'text', name: 'Text' },
  { key: 'graphics', name: 'Graphics' },
  { key: 'voice', name: 'Narration' },
  { key: 'audio', name: 'Sound effects' },
];
const LANE_H = { scenes: 54, video: 46, text: 30, graphics: 30, voice: 30, audio: 24 };
const EDITABLE_TRACKS = new Set(['video', 'text', 'graphics']);
const TEXT_GRAPHICS = new Set(['word', 'title', 'kinetic', 'quote', 'lower_third', 'chapter']);
const FPS = 30;
const SNAP_PX = 8;
const GRID = ['top_left', 'top_center', 'top_right', 'middle_left', 'center', 'middle_right', 'bottom_left',
  'bottom_center', 'bottom_right'];

function fmt(t) {
  if (!Number.isFinite(t)) return '0:00.0';
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  return `${m}:${s < 10 ? '0' : ''}${s.toFixed(1)}`;
}
const frame = (t) => Math.round(t * FPS) / FPS;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function host(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch (_) { return url; }
}

function newId() {
  return `u_${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-4)}`;
}

/** Put overlapping blocks of one track on separate lanes (greedy, by start time). */
function lanes(items, tol = 0.001) {
  const ends = [];
  const out = new Map();
  [...items].sort((a, b) => a.start - b.start).forEach((it) => {
    let lane = ends.findIndex((e) => e <= it.start + tol);
    if (lane < 0) { lane = ends.length; ends.push(0); }
    ends[lane] = it.end;
    out.set(it.id, lane);
  });
  return { map: out, count: Math.max(1, ends.length) };
}

// thumbnails: fetched with the login token (an <img> can't send it), a few at a time, kept for the session
const thumbCache = new Map();
const queue = [];
let active = 0;
function finished() { active -= 1; pump(); } // eslint-disable-line no-use-before-define
function run(job) {
  active += 1;
  api.get(job.path, { params: job.params, responseType: 'blob' })
    .then((r) => URL.createObjectURL(r.data))
    .catch(() => null)
    .then((url) => { job.resolve(url); finished(); });
}
function pump() {
  while (active < 4 && queue.length) run(queue.shift());
}
function loadThumb(videoId, params) {
  const key = `${videoId}|${JSON.stringify(params)}`;
  if (!thumbCache.has(key)) {
    thumbCache.set(key, new Promise((resolve) => {
      queue.push({ path: `/visual-engine/videos/${videoId}/thumb`, params, resolve });
      pump();
    }));
  }
  return thumbCache.get(key);
}

function useThumb(videoId, params, ref) {
  const [url, setUrl] = useState(null);
  const key = params ? JSON.stringify(params) : '';
  useEffect(() => {
    if (!params) return undefined;
    let alive = true;
    const el = ref?.current;
    const go = () => loadThumb(videoId, params).then((u) => { if (alive) setUrl(u); });
    if (!el || !('IntersectionObserver' in window)) { go(); return () => { alive = false; }; }
    const io = new IntersectionObserver((ents) => {
      if (ents.some((e) => e.isIntersecting)) { go(); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => { alive = false; io.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, key]);
  return url;
}

const GRAPHIC_NAMES = { backdrop: 'Background', lower_third: 'Name caption', bar_chart: 'Bar chart', line_chart: 'Line chart',
  area_chart: 'Area chart', floor_plan: 'Floor plan', evidence_board: 'Evidence board', case_card: 'Case card',
  call_transcript: 'Call transcript', kinetic: 'Kinetic text' };
export const niceGraphic = (g) => (g ? GRAPHIC_NAMES[g] || g.charAt(0).toUpperCase() + g.slice(1).replace(/_/g, ' ') : '');
const blockLabel = (it) => (it.label && it.label !== it.graphic ? it.label : niceGraphic(it.graphic) || it.label || it.kind);
const nice = (s) => String(s ?? '').replace(/_/g, ' ');
const kindName = (it, asset) => (it.track === 'video' ? (asset?.kind === 'video' ? 'Video clip' : 'Photo')
  : it.track === 'text' ? (it.graphic ? `Text: ${niceGraphic(it.graphic)}` : 'Text')
    : it.track === 'graphics' ? `Graphic${it.graphic ? `: ${niceGraphic(it.graphic)}` : ''}`
      : it.track === 'audio' ? 'Sound effect' : 'Element');

// ── changes (edits) ──────────────────────────────────────────────────────────────────────────────────────────

const LABEL_PATHS = new Set(['text', 'data.text', 'data.name', 'data.title', 'data.headline', 'data.label', 'data.value']);
const SAME = {  // a newer change of the same thing replaces the older one
  set: (a, b) => a.path === b.path,
  style: (a, b) => a.key === b.key,
  timing: () => true,
  swap: () => true,
};

/** Add a change; one that only replaces an earlier one of the same kind takes its place in the list. */
function upsert(list, e) {
  const same = SAME[e.type];
  if (same) {
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const o = list[i];
      if (o._item === e._item && o.type === e.type && same(o, e)) {
        // not past a split or copy of the same element: those used the old value
        const later = list.slice(i + 1).some((x) => x._item === e._item && ['split', 'duplicate'].includes(x.type));
        if (!later) { const n = [...list]; n[i] = { ...e, _k: o._k }; return n; }
        break;
      }
    }
  }
  return [...list, { ...e, _k: newId() }];
}

/** Remove one change. Removing an "add", "copy" or "split" also removes everything later done to the element it made. */
function withoutChange(list, e) {
  const gone = new Set();
  const child = (x) => (x.new_id ? `${x.scene_id}/${x.composition}/${x.new_id}` : null);
  if (child(e)) gone.add(child(e));
  return list.filter((x) => {
    if (x._k === e._k) return false;
    if (gone.has(x._item)) { if (child(x)) gone.add(child(x)); return false; }
    return true;
  });
}

const clean = (edits) => edits.map((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith('_'))));

function trackForLayer(layer) {
  if (layer.type === 'media') return 'video';
  if (layer.type === 'text') return 'text';
  return TEXT_GRAPHICS.has(layer.graphic) ? 'text' : 'graphics';
}

/** The timeline as it will be after the changes (what the user sees while editing). */
function projectItems(base, edits, ctx) {
  const list = base.map((x) => ({ ...x }));
  const idx = (id) => list.findIndex((x) => x.id === id);
  edits.forEach((e) => {
    const i = idx(e._item);
    const it = i >= 0 ? list[i] : null;
    const childId = e.new_id ? `${e.scene_id}/${e.composition}/${e.new_id}` : null;
    if (e.type === 'add') {
      const entry = ctx.palette.find((p) => p.id === e._palette) || {};
      const fields = (entry.fields || []).map((f) => ({ ...f }));
      const scene = ctx.scenes.find((s) => s.id === e.scene_id);
      list.push({
        id: childId, track: trackForLayer(e.layer), kind: e.layer.type, scene_id: e.scene_id, composition: e.composition,
        layer_id: e.new_id, start: e.start, end: e.end, graphic: e.layer.graphic,
        label: e.layer.text || (e.layer.type === 'media' ? `Picture from ${host(e.layer.url)}` : niceGraphic(e.layer.graphic)),
        fields, style: { ...(entry.style || {}), ...(e.layer.slot ? { position: e.layer.slot } : {}) },
        style_kind: e.layer.type === 'media' ? 'media' : e.layer.type, variants: entry.variants || [],
        bounds: scene ? [scene.start, scene.end] : [e.start, e.end], editable: true, added: true, z: 99,
        swappable: e.layer.type === 'media', new_url: e.layer.url, notes: [], filled: [],
      });
      return;
    }
    if (!it) return;
    if (e.type === 'set') {
      it.fields = (it.fields || []).map((f) => (f.path === e.path ? { ...f, value: e.value } : f));
      if (LABEL_PATHS.has(e.path)) it.label = String(e.value).slice(0, 60);
    } else if (e.type === 'style') {
      it.style = { ...(it.style || {}), [e.key]: e.value };
    } else if (e.type === 'timing') {
      it.start = e.start; it.end = e.end;
    } else if (e.type === 'swap') {
      it.new_url = e.url;
    } else if (e.type === 'delete') {
      list.splice(i, 1);
    } else if (e.type === 'duplicate') {
      const copy = { ...it, id: childId, layer_id: e.new_id, added: true };
      if (e.start != null) { copy.start = e.start; copy.end = e.end; }
      list.splice(i + 1, 0, copy);
    } else if (e.type === 'split') {
      const copy = { ...it, id: childId, layer_id: e.new_id, added: true, start: e.at };
      it.end = e.at;
      list.splice(i + 1, 0, copy);
    } else if (e.type === 'order') {
      it.z = e.direction === 'front' ? 999 : e.direction === 'back' ? -1 : (it.z || 0) + (e.direction === 'up' ? 1 : -1);
    }
  });
  return list;
}

function describe(e, items) {
  const it = items.find((x) => x.id === e._item);
  const name = e._name || (it ? blockLabel(it) : e.layer_id);
  switch (e.type) {
    case 'set': return `${name}: ${e._field || e.path} → “${String(e.value).slice(0, 28)}”`;
    case 'style': return `${name}: ${e._field || nice(e.key)} → ${nice(e.value)}`;
    case 'timing': return `${name}: ${fmt(e.start)} to ${fmt(e.end)}`;
    case 'swap': return `${name}: new picture from ${host(e.url)}`;
    case 'delete': return `${name}: removed`;
    case 'duplicate': return `${name}: copied`;
    case 'split': return `${name}: split at ${fmt(e.at)}`;
    case 'order': return `${name}: ${e.direction === 'front' ? 'brought to front' : e.direction === 'back' ? 'sent to back' : e.direction === 'up' ? 'brought forward' : 'sent backward'}`;
    case 'add': return `Added ${e.layer.type === 'media' ? 'a picture' : e.layer.type === 'text' ? 'text' : niceGraphic(e.layer.graphic)} in ${e.scene_id}`;
    default: return e.type;
  }
}

// ── blocks ───────────────────────────────────────────────────────────────────────────────────────────────────

function Block({ videoId, it, pps, lane, laneH, selected, changed, live, onPointerDown }) {
  const ref = useRef(null);
  const thumbParams = it.track === 'scenes' ? { scene: it.id } : (it.asset_id && !it.new_url ? { asset: it.asset_id } : null);
  const thumb = useThumb(videoId, thumbParams, ref);
  const start = live ? live.start : it.start;
  const end = live ? live.end : it.end;
  const left = start * pps;
  const width = Math.max(4, (end - start) * pps);
  const canEdit = it.editable && EDITABLE_TRACKS.has(it.track);
  return (
    <div role="button" tabIndex={0} ref={ref}
         className={`dtl-block dtl-block--${it.track} ${selected ? 'is-sel' : ''} ${changed ? 'is-changed' : ''} ${canEdit ? 'is-edit' : ''} ${live ? 'is-drag' : ''} ${it.added ? 'is-new' : ''}`}
         style={{ left, width, top: lane * laneH + 3, height: laneH - 6 }}
         onPointerDown={(e) => onPointerDown(e, it, 'move')}
         title={`${blockLabel(it)} (${fmt(start)} to ${fmt(end)})${canEdit ? ' · drag to move, drag an edge to trim' : ''}`}>
      {thumb && <span className="dtl-block__img" style={{ backgroundImage: `url(${thumb})` }} />}
      {width > 34 && <span className="dtl-block__label">{blockLabel(it)}</span>}
      {changed && <span className="dtl-block__dot" aria-label="changed" />}
      {canEdit && width > 14 && (
        <>
          <span className="dtl-handle dtl-handle--l" onPointerDown={(e) => onPointerDown(e, it, 'start')} />
          <span className="dtl-handle dtl-handle--r" onPointerDown={(e) => onPointerDown(e, it, 'end')} />
        </>
      )}
    </div>
  );
}

function Ruler({ duration, pps, onSeek }) {
  const step = pps > 120 ? 1 : pps > 50 ? 2 : pps > 25 ? 5 : pps > 10 ? 10 : 30;
  const ticks = [];
  for (let t = 0; t <= duration + 0.001; t += step) ticks.push(t);
  return (
    <div className="dtl-ruler" style={{ width: duration * pps }} onPointerDown={onSeek}>
      {ticks.map((t) => (
        <span key={t} className="dtl-tick" style={{ left: t * pps }}>
          <span>{fmt(t).replace(/\.0$/, '')}</span>
        </span>
      ))}
    </div>
  );
}

// ── inspector ────────────────────────────────────────────────────────────────────────────────────────────────

function AssetPreview({ videoId, assetId }) {
  const ref = useRef(null);
  const url = useThumb(videoId, assetId ? { asset: assetId } : null, ref);
  return (
    <div className="dtl-insp__preview" ref={ref}>
      {url ? <img src={url} alt="" /> : <span>{assetId ? 'Loading preview…' : 'New picture: shown after rendering'}</span>}
    </div>
  );
}

function NumberInput({ value, onCommit, step = 0.1, min, max, label }) {
  const [txt, setTxt] = useState(value == null ? '' : String(value));
  useEffect(() => { setTxt(value == null ? '' : String(value)); }, [value]);
  const commit = () => {
    const v = parseFloat(txt);
    if (!Number.isFinite(v)) { setTxt(value == null ? '' : String(value)); return; }
    const c = clamp(v, min ?? -Infinity, max ?? Infinity);
    if (c !== value) onCommit(c); else setTxt(String(value));
  };
  return (
    <input className="docs-input dtl-num" inputMode="decimal" value={txt} step={step} aria-label={label}
           onChange={(e) => setTxt(e.target.value)} onBlur={commit}
           onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); } }} />
  );
}

function StyleControl({ opt, value, variants, changed, onChange }) {
  const head = <span>{opt.label}{changed && <em> changed</em>}</span>;
  if (opt.kind === 'position') {
    return (
      <div className={`dtl-field ${changed ? 'is-changed' : ''}`}>
        {head}
        <div className="dtl-posgrid" role="radiogroup" aria-label={opt.label}>
          {GRID.map((p) => (
            <button key={p} type="button" role="radio" aria-checked={value === p} title={nice(p)}
                    className={`dtl-pos ${value === p ? 'is-on' : ''}`} onClick={() => onChange(p)}>
              <i />
            </button>
          ))}
        </div>
        {value === 'custom' && <small className="dtl-insp__hint">Placed by the director at an exact spot. Pick a square to move it.</small>}
      </div>
    );
  }
  if (opt.kind === 'choice') {
    const choices = opt.key === 'variant' ? variants : opt.choices;
    if (!choices?.length) return null;
    return (
      <label className={`dtl-field ${changed ? 'is-changed' : ''}`}>
        {head}
        <select className="docs-input dtl-select" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          {value == null && <option value="">Default</option>}
          {choices.map((c) => <option key={c} value={c}>{nice(c)}</option>)}
        </select>
      </label>
    );
  }
  if (opt.kind === 'color') {
    const v = /^#[0-9a-f]{6}$/i.test(value || '') ? value : '';
    return (
      <label className={`dtl-field dtl-field--row ${changed ? 'is-changed' : ''}`}>
        {head}
        <span className="dtl-color">
          <input type="color" value={v || '#ffffff'} onChange={(e) => onChange(e.target.value.toUpperCase())} />
          <code>{v || 'Default'}</code>
        </span>
      </label>
    );
  }
  if (opt.kind === 'bool') {
    return (
      <label className={`dtl-field dtl-field--row ${changed ? 'is-changed' : ''}`}>
        {head}
        <input type="checkbox" className="dtl-check" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
      </label>
    );
  }
  if (opt.kind === 'number') {
    const v = Number.isFinite(Number(value)) ? Number(value) : opt.min;
    return (
      <div className={`dtl-field ${changed ? 'is-changed' : ''}`}>
        {head}
        <div className="dtl-range">
          <input type="range" min={opt.min} max={opt.max} step={opt.step} value={v}
                 onChange={(e) => onChange(Number(e.target.value))} aria-label={opt.label} />
          <NumberInput value={v} min={opt.min} max={opt.max} step={opt.step} label={opt.label} onCommit={onChange} />
        </div>
      </div>
    );
  }
  return null;
}

function Inspector({ videoId, data, item, scene, edits, addEdit, onSeek, t, tab, setTab, actions }) {
  const asset = item?.asset_id && !item.new_url ? data.assets?.[item.asset_id] : null;
  const [swapUrl, setSwapUrl] = useState('');
  const [pick, setPick] = useState('');
  const [clip, setClip] = useState(['', '']);

  useEffect(() => {
    setSwapUrl(''); setPick(''); setClip(['', '']);
  }, [item?.id]);

  if (!item) {
    if (!scene) return <div className="dtl-insp dtl-insp--empty">Click anything on the timeline to see where it came from and change it.</div>;
    return (
      <div className="dtl-insp">
        <div className="dtl-insp__kind">Scene at the playhead</div>
        <h3>{scene.purpose || scene.id}</h3>
        <p className="dtl-insp__time">{fmt(scene.start)} to {fmt(scene.end)}</p>
        {scene.narration && <blockquote className="dtl-insp__narr">{scene.narration}</blockquote>}
        {scene.placeholder && <p className="dtl-insp__warn">This scene failed to render and was replaced by a placeholder.</p>}
        <p className="dtl-insp__hint">Click a picture, title or graphic on the timeline to edit it, or press “Add” to put
          something new into this scene.</p>
      </div>
    );
  }

  const editable = item.editable && EDITABLE_TRACKS.has(item.track);
  const base = { scene_id: item.scene_id, composition: item.composition, layer_id: item.layer_id, _item: item.id, _name: blockLabel(item) };
  const mine = edits.filter((e) => e._item === item.id);
  const changedPath = new Set(mine.filter((e) => e.type === 'set').map((e) => e.path));
  const changedKey = new Set(mine.filter((e) => e.type === 'style').map((e) => e.key));
  const opts = (data.options?.[item.style_kind] || []).filter((o) => !(o.video_only && asset?.kind !== 'video')
    && !(o.image_only && asset?.kind === 'video'));

  const queueSwap = () => {
    const url = swapUrl.trim();
    if (!/^https?:\/\/\S{4,}$/i.test(url)) { toast.error('Paste a full link that starts with https://'); return; }
    const e = { ...base, type: 'swap', url };
    if (pick.trim()) e.pick = pick.trim();
    const a = parseFloat(clip[0]); const b = parseFloat(clip[1]);
    if (Number.isFinite(a) && Number.isFinite(b) && b > a && a >= 0) e.clip = [a, b];
    addEdit(e);
    toast.info('Picture swap added to your changes.');
  };
  const retime = (start, end) => {
    const [b0, b1] = item.bounds || [item.start, item.end];
    const s = frame(clamp(start, b0, b1));
    const en = frame(clamp(end, b0, b1));
    if (en - s < (data.limits?.min_len || 0.2) - 1e-6) { toast.error('That would make the element too short.'); return; }
    addEdit({ ...base, type: 'timing', start: s, end: en });
  };
  const nudge = (d) => {  // a move keeps the length; at the edge of the scene it stops there
    const [b0, b1] = item.bounds || [item.start, item.end];
    const dd = clamp(d, b0 - item.start, b1 - item.end);
    if (Math.abs(dd) < 1e-4) { toast.info('It is already at the edge of its scene.'); return; }
    retime(item.start + dd, item.end + dd);
  };

  return (
    <div className="dtl-insp">
      <div className="dtl-insp__kind">{kindName(item, asset)}{item.added && <span className="dtl-tag">new</span>}</div>
      <h3>{blockLabel(item)}</h3>
      <button type="button" className="dtl-insp__time docs-link" onClick={() => onSeek(item.start)}>
        {fmt(item.start)} to {fmt(item.end)} in {item.scene_id}
      </button>

      {editable && (
        <div className="dtl-acts">
          <button type="button" className="docs-btn docs-btn--small" onClick={actions.split} title="Split at the playhead (S)">Split</button>
          <button type="button" className="docs-btn docs-btn--small" onClick={actions.duplicate} title="Copy (Ctrl+D)">Copy</button>
          <button type="button" className="docs-btn docs-btn--small" onClick={() => actions.order('up')} title="Bring forward">Forward</button>
          <button type="button" className="docs-btn docs-btn--small" onClick={() => actions.order('down')} title="Send backward">Back</button>
          <button type="button" className="docs-btn docs-btn--small dtl-danger" onClick={actions.remove} title="Delete (Del)">Delete</button>
        </div>
      )}

      {editable ? (
        <div className="dtl-tabs" role="tablist">
          {[['content', 'Content'], ['timing', 'Timing'], ['look', 'Look'], ['notes', 'Notes']].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={`dtl-tab ${tab === k ? 'is-on' : ''}`}
                    onClick={() => setTab(k)}>
              {label}{k === 'notes' && item.notes?.length ? <span className="dtl-count">{item.notes.length}</span> : null}
            </button>
          ))}
        </div>
      ) : (
        <p className="dtl-insp__hint">
          {item.from_pattern ? 'This element comes from a layout the director picked; it can’t be edited one by one.'
            : item.track === 'audio' ? 'Sound effects can’t be edited here yet.' : 'This element can’t be edited.'}
        </p>
      )}

      {editable && tab === 'content' && (
        <>
          {item.track === 'video' && (
            <>
              <AssetPreview videoId={videoId} assetId={item.new_url ? null : item.asset_id} />
              {item.new_url && <p className="dtl-insp__hint">New picture from <a href={item.new_url} target="_blank" rel="noreferrer">{host(item.new_url)}</a>. It is downloaded when you render.</p>}
              {asset ? (
                <dl className="dtl-insp__facts">
                  {asset.page_url || asset.url ? (
                    <div><dt>Source</dt><dd><a href={asset.page_url || asset.url} target="_blank" rel="noreferrer">
                      {asset.source ? `${asset.source}, ` : ''}{host(asset.page_url || asset.url)}</a></dd></div>
                  ) : asset.source ? <div><dt>Source</dt><dd>{asset.source}</dd></div> : null}
                  {asset.title && <div><dt>Title</dt><dd>{asset.title}</dd></div>}
                  <div><dt>Licence</dt><dd>{asset.license || 'Not recorded'}</dd></div>
                  {asset.credit && <div><dt>Credit</dt><dd>{asset.credit}</dd></div>}
                  {asset.width && <div><dt>Size</dt><dd>{asset.width} by {asset.height}{asset.duration ? `, ${Math.round(asset.duration)} s` : ''}</dd></div>}
                </dl>
              ) : !item.new_url && <p className="dtl-insp__hint">No source recorded for this element.</p>}
              {item.swappable && (
                <div className="dtl-swap">
                  <h4>Swap this {asset?.kind === 'video' ? 'clip' : 'picture'}</h4>
                  <p className="dtl-insp__hint">Paste a link to a photo, a page with the photo, or a video (YouTube and similar
                    work too). Watermarked pictures are refused.</p>
                  <input className="docs-input" placeholder="https://" value={swapUrl} onChange={(e) => setSwapUrl(e.target.value)} />
                  <input className="docs-input" placeholder="Words from the caption, if the page has many pictures (optional)"
                         value={pick} onChange={(e) => setPick(e.target.value)} />
                  <div className="dtl-swap__clip">
                    <span>For a video, use from</span>
                    <input className="docs-input" inputMode="decimal" placeholder="0" value={clip[0]}
                           onChange={(e) => setClip([e.target.value, clip[1]])} aria-label="Clip start in seconds" />
                    <span>to</span>
                    <input className="docs-input" inputMode="decimal" placeholder="10" value={clip[1]}
                           onChange={(e) => setClip([clip[0], e.target.value])} aria-label="Clip end in seconds" />
                    <span>seconds</span>
                  </div>
                  <div className="dtl-swap__actions">
                    <button type="button" className="docs-btn docs-btn--primary" onClick={queueSwap}>Swap picture</button>
                  </div>
                </div>
              )}
            </>
          )}
          {(item.track === 'text' || item.track === 'graphics') && (
            item.fields?.length ? (
              <div className="dtl-fields">
                {item.fields.map((f) => {
                  const changed = changedPath.has(f.path);
                  const set = (value) => addEdit({ ...base, type: 'set', path: f.path, value, _field: f.label }, `set|${item.id}|${f.path}`);
                  return (
                    <label key={f.path} className={`dtl-field ${changed ? 'is-changed' : ''}`}>
                      <span>{f.label}{changed && <em> changed</em>}</span>
                      {f.kind === 'number' ? (
                        <NumberInput value={f.value} label={f.label} onCommit={set} />
                      ) : f.multiline ? (
                        <textarea className="docs-textarea" rows={3} value={f.value} onChange={(e) => set(e.target.value)} />
                      ) : (
                        <input className="docs-input" value={f.value} onChange={(e) => set(e.target.value)} />
                      )}
                    </label>
                  );
                })}
              </div>
            ) : <p className="dtl-insp__hint">This element has no words or numbers to change.</p>
          )}
        </>
      )}

      {editable && tab === 'timing' && (
        <div className="dtl-timing">
          <div className="dtl-timing__row">
            <label><span>Starts</span>
              <NumberInput value={Number(item.start.toFixed(2))} label="Start in seconds" step={1 / FPS}
                           onCommit={(v) => retime(v, item.end)} /></label>
            <button type="button" className="docs-btn docs-btn--small" onClick={() => retime(t, item.end)}>At playhead</button>
          </div>
          <div className="dtl-timing__row">
            <label><span>Ends</span>
              <NumberInput value={Number(item.end.toFixed(2))} label="End in seconds" step={1 / FPS}
                           onCommit={(v) => retime(item.start, v)} /></label>
            <button type="button" className="docs-btn docs-btn--small" onClick={() => retime(item.start, t)}>At playhead</button>
          </div>
          <div className="dtl-timing__row">
            <span className="dtl-timing__lbl">Move</span>
            <button type="button" className="docs-btn docs-btn--small" onClick={() => nudge(-10 / FPS)}>−10 frames</button>
            <button type="button" className="docs-btn docs-btn--small" onClick={() => nudge(-1 / FPS)}>−1</button>
            <button type="button" className="docs-btn docs-btn--small" onClick={() => nudge(1 / FPS)}>+1</button>
            <button type="button" className="docs-btn docs-btn--small" onClick={() => nudge(10 / FPS)}>+10 frames</button>
          </div>
          <p className="dtl-insp__hint">On screen for {(item.end - item.start).toFixed(2)} s. It can move anywhere inside its scene
            ({fmt(item.bounds?.[0])} to {fmt(item.bounds?.[1])}). On the timeline, drag the block to move it and drag
            its edges to trim; edges snap to spoken words, the playhead and other elements (hold Alt to stop snapping).</p>
          {(item.locked?.start || item.locked?.end) && (
            <p className="dtl-insp__hint">The director tied this element to a spoken word. If you move it, it is pinned to the new time instead.</p>
          )}
        </div>
      )}

      {editable && tab === 'look' && (
        <div className="dtl-fields">
          {opts.length ? opts.map((o) => (
            <StyleControl key={o.key} opt={o} value={item.style?.[o.key]} variants={item.variants}
                          changed={changedKey.has(o.key)}
                          onChange={(v) => addEdit({ ...base, type: 'style', key: o.key, value: v, _field: o.label }, `style|${item.id}|${o.key}`)} />
          )) : <p className="dtl-insp__hint">Nothing to style on this element.</p>}
        </div>
      )}

      {editable && tab === 'notes' && (
        <div className="dtl-notes">
          {item.notes?.length ? (
            <>
              <h4>What the engine fixed at the last render</h4>
              <ul>{item.notes.map((n) => <li key={n}>{n}</li>)}</ul>
            </>
          ) : <p className="dtl-insp__hint">The engine did not need to fix anything on this element.</p>}
          {item.filled?.length ? (
            <p className="dtl-insp__hint">Settings the director left empty, filled in by the engine: {item.filled.map(nice).join(', ')}.</p>
          ) : null}
          {item.edited?.length ? (
            <p className="dtl-insp__hint">Changed by you earlier: {item.edited.map((x) => nice(x.split('.').pop())).join(', ')}.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ── add panel ────────────────────────────────────────────────────────────────────────────────────────────────

const GROUP_NAMES = { text: 'Text', type: 'Titles and words', data: 'Numbers and charts', structure: 'Lists and steps',
  story: 'Story', annotation: 'Pointers on footage', media: 'Pictures and clips' };

function AddPanel({ palette, scene, onAdd, onClose }) {
  const [q, setQ] = useState('');
  const [media, setMedia] = useState(null);
  const [url, setUrl] = useState('');
  const groups = useMemo(() => {
    const m = new Map();
    palette.filter((p) => !q || `${p.label} ${p.summary} ${p.id}`.toLowerCase().includes(q.toLowerCase()))
      .forEach((p) => { if (!m.has(p.group)) m.set(p.group, []); m.get(p.group).push(p); });
    return [...m.entries()].sort((a, b) => Object.keys(GROUP_NAMES).indexOf(a[0]) - Object.keys(GROUP_NAMES).indexOf(b[0]));
  }, [palette, q]);
  if (!scene) {
    return (
      <div className="dtl-insp">
        <p className="dtl-insp__hint">Move the playhead into a scene first: new elements are added there.</p>
        <button type="button" className="docs-btn docs-btn--small" onClick={onClose}>Close</button>
      </div>
    );
  }
  return (
    <div className="dtl-insp dtl-add">
      <div className="dtl-add__head">
        <div>
          <div className="dtl-insp__kind dtl-insp__kind--plain">Add to {scene.purpose || scene.id}</div>
          <p className="dtl-insp__hint">It starts at the playhead. Drag it on the timeline afterwards.</p>
        </div>
        <button type="button" className="dtl-x" onClick={onClose} aria-label="Close">×</button>
      </div>
      {media ? (
        <div className="dtl-swap">
          <h4>{media.label}</h4>
          <p className="dtl-insp__hint">{media.summary} Watermarked pictures are refused.</p>
          <input className="docs-input" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus />
          <div className="dtl-swap__actions">
            <button type="button" className="docs-btn docs-btn--primary" onClick={() => {
              if (!/^https?:\/\/\S{4,}$/i.test(url.trim())) { toast.error('Paste a full link that starts with https://'); return; }
              onAdd(media, { url: url.trim() });
            }}>Add picture</button>
            <button type="button" className="docs-btn docs-btn--ghost" onClick={() => setMedia(null)}>Back</button>
          </div>
        </div>
      ) : (
        <>
          <input className="docs-input" placeholder="Search: chart, map, quote…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          <div className="dtl-add__list">
            {groups.map(([g, list]) => (
              <section key={g}>
                <h4>{GROUP_NAMES[g] || nice(g)}</h4>
                <div className="dtl-add__grid">
                  {list.map((p) => (
                    <button key={p.id} type="button" className="dtl-add__item" title={p.summary}
                            onClick={() => (p.type === 'media' ? setMedia(p) : onAdd(p))}>
                      <strong>{p.type === 'graphic' ? niceGraphic(p.id) : p.label}</strong>
                      <span>{p.summary}</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
            {!groups.length && <p className="dtl-insp__hint">Nothing matches.</p>}
          </div>
        </>
      )}
    </div>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────────────────

const draftKey = (id) => `ve-timeline-draft-${id}`;

export default function TimelineEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [pps, setPps] = useState(40);
  const [selId, setSelId] = useState(null);
  const [tab, setTab] = useState('content');
  const [adding, setAdding] = useState(false);
  const [hist, setHist] = useState({ past: [], now: [], future: [] });
  const [sending, setSending] = useState(false);
  const [drag, setDrag] = useState(null); // {id, start, end, snap}
  const [showPreview, setShowPreview] = useState(true);
  const [safeArea, setSafeArea] = useState(false);
  const [preview, setPreview] = useState({ url: null, loading: false, error: '' });
  const vref = useRef(null);
  const scrollRef = useRef(null);
  const raf = useRef(0);
  const dragging = useRef(false);
  const coalesce = useRef({ key: null, at: 0 });
  const edits = hist.now;

  useEffect(() => {
    Promise.all([api.get(`/visual-engine/videos/${id}`), api.get(`/visual-engine/videos/${id}/timeline`)])
      .then(([v, tl]) => {
        setVideo(v.data.video);
        setData(tl.data);
        try {  // unsaved changes from an earlier visit (same version of the video)
          const d = JSON.parse(localStorage.getItem(draftKey(id)) || 'null');
          if (d && d.version === (tl.data.versions?.length || 1) && Array.isArray(d.edits) && d.edits.length) {
            setHist({ past: [], now: d.edits, future: [] });
            toast.info(`Restored ${d.edits.length} unsaved change${d.edits.length === 1 ? '' : 's'}.`);
          }
        } catch (_) { /* storage blocked */ }
      })
      .catch((e) => setError(errorMessage(e, 'Could not open the timeline.')));
  }, [id]);

  // keep unsaved changes across a reload, and warn before leaving with them
  useEffect(() => {
    if (!data) return undefined;
    try {
      if (edits.length) localStorage.setItem(draftKey(id), JSON.stringify({ version: data.versions?.length || 1, edits }));
      else localStorage.removeItem(draftKey(id));
    } catch (_) { /* storage blocked */ }
    if (!edits.length) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [edits, data, id]);

  /** Change the list of changes. Typing / dragging a slider coalesces into one undo step. */
  const change = useCallback((fn, key) => {
    setHist((h) => {
      const now = fn(h.now);
      const c = coalesce.current;
      const merge = key && c.key === key && Date.now() - c.at < 1200;
      coalesce.current = { key: key || null, at: Date.now() };
      return merge ? { ...h, now, future: [] } : { past: [...h.past, h.now].slice(-200), now, future: [] };
    });
  }, []);
  const addEdit = useCallback((e, key) => change((list) => upsert(list, e), key), [change]);
  const undo = useCallback(() => setHist((h) => (h.past.length ? { past: h.past.slice(0, -1), now: h.past[h.past.length - 1], future: [h.now, ...h.future] } : h)), []);
  const redo = useCallback(() => setHist((h) => (h.future.length ? { past: [...h.past, h.now], now: h.future[0], future: h.future.slice(1) } : h)), []);

  const duration = data?.duration || 0;
  const fitted = useRef(false);
  useLayoutEffect(() => {  // open with the whole video in view
    if (fitted.current || !duration || !scrollRef.current) return;
    fitted.current = true;
    setPps(Math.max(5, Math.min(120, (scrollRef.current.clientWidth - 48) / duration)));
  }, [duration, video]);

  const scenes = useMemo(() => data?.scenes || [], [data]);
  const items = useMemo(() => {
    if (!data) return [];
    const sc = scenes.map((s) => ({ ...s, track: 'scenes', label: s.purpose || s.id }));
    return [...sc, ...projectItems(data.items || [], edits, { palette: data.palette || [], scenes })];
  }, [data, edits, scenes]);
  const byTrack = useMemo(() => {
    const m = {};
    TRACKS.forEach(({ key }) => { m[key] = items.filter((i) => i.track === key); });
    return m;
  }, [items]);
  const laneInfo = useMemo(() => {
    const m = {};
    // fades make neighbours overlap by a fraction of a second: they still share a row
    TRACKS.forEach(({ key }) => {
      m[key] = key === 'voice' ? { map: new Map(), count: 1 } : lanes(byTrack[key] || [], key === 'scenes' ? 0.6 : 0.2);
    });
    return m;
  }, [byTrack]);
  const changedItems = useMemo(() => {
    const s = new Set();
    edits.forEach((e) => { if (e._item) s.add(e._item); if (e.new_id) s.add(`${e.scene_id}/${e.composition}/${e.new_id}`); });
    return s;
  }, [edits]);
  const changedScenes = useMemo(() => [...new Set(edits.map((e) => e.scene_id))], [edits]);
  const sceneAt = useMemo(() => scenes.find((s) => t >= s.start && t < s.end) || (t >= duration && scenes[scenes.length - 1]), [scenes, t, duration]);
  const sel = useMemo(() => items.find((x) => x.id === selId) || null, [items, selId]);

  // playhead follows the video smoothly while it plays
  const tick = useCallback(() => {
    const v = vref.current;
    if (v) setT(v.currentTime);
    raf.current = requestAnimationFrame(tick);
  }, []);
  useEffect(() => {
    if (playing) raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, tick]);

  // keep the playhead in view while playing
  useLayoutEffect(() => {
    const sc = scrollRef.current;
    if (!sc || !playing || dragging.current) return;
    const x = t * pps;
    if (x > sc.scrollLeft + sc.clientWidth * 0.85 || x < sc.scrollLeft) sc.scrollLeft = Math.max(0, x - sc.clientWidth * 0.2);
  }, [t, pps, playing]);

  const seek = useCallback((time) => {
    const v = vref.current;
    const tt = Math.max(0, Math.min(duration || 0, time));
    if (v) v.currentTime = tt;
    setT(tt);
  }, [duration]);

  const toggle = useCallback(() => {
    const v = vref.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  }, []);

  // ── live preview: a real frame of the scene under the playhead with the changes applied ──
  const previewScene = sceneAt && changedScenes.includes(sceneAt.id) ? sceneAt.id : null;
  const tFrame = frame(t);
  useEffect(() => {
    if (playing || !previewScene || !showPreview) { setPreview((p) => (p.url || p.loading || p.error ? { url: null, loading: false, error: '' } : p)); return undefined; }
    const ctl = new AbortController();
    setPreview((p) => ({ ...p, loading: true, error: '' }));
    const timer = setTimeout(() => {
      api.post(`/visual-engine/videos/${id}/preview`, { scene_id: previewScene, t: tFrame, edits: clean(edits) },
        { responseType: 'blob', signal: ctl.signal })
        .then((r) => {
          const url = URL.createObjectURL(r.data);
          setPreview((p) => { if (p.url) URL.revokeObjectURL(p.url); return { url, loading: false, error: '' }; });
        })
        .catch(async (e) => {
          if (ctl.signal.aborted) return;
          let msg = 'The preview could not be made.';
          try {  // errors come back as a blob too
            const txt = await e?.response?.data?.text?.();
            msg = JSON.parse(txt)?.error?.message || msg;
          } catch (_) { msg = errorMessage(e, msg); }
          setPreview((p) => ({ ...p, loading: false, error: msg }));
        });
    }, 380);
    return () => { clearTimeout(timer); ctl.abort(); };
  }, [id, edits, previewScene, tFrame, playing, showPreview]);

  // scrub: drag on the ruler or the playhead
  const scrubAt = (clientX) => {
    const sc = scrollRef.current;
    if (!sc) return;
    const rect = sc.getBoundingClientRect();
    seek((clientX - rect.left + sc.scrollLeft) / pps);
  };
  const onScrubStart = (e) => {
    dragging.current = true;
    scrubAt(e.clientX);
    const move = (ev) => scrubAt(ev.clientX);
    const up = () => { dragging.current = false; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  /** Select an element; the playhead goes where it was clicked (or a moment into it, where it is fully on screen). */
  const select = useCallback((it, at) => {
    if (it.track === 'scenes') { setSelId(null); seek(at ?? it.start + 0.01); return; }
    setAdding(false);
    setSelId(it.id);
    if (at != null) seek(clamp(at, it.start, it.end - 1 / FPS));
    else if (t < it.start || t >= it.end) seek(it.start + Math.min(0.8, (it.end - it.start) / 2));
  }, [seek, t]);

  // ── drag to move / trim, with snapping ──
  const onBlockPointerDown = (e, it, mode) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const editable = it.editable && EDITABLE_TRACKS.has(it.track);
    const x0 = e.clientX;
    const s0 = it.start;
    const e0 = it.end;
    const len = e0 - s0;
    const [b0, b1] = it.bounds || [s0, e0];
    const minLen = data?.limits?.min_len || 0.2;
    const marks = [b0, b1, t];
    (data?.words || []).forEach((w) => { if (w.t >= b0 - 0.01 && w.t <= b1 + 0.01) marks.push(w.t, w.e); });
    items.forEach((o) => { if (o.id !== it.id && o.scene_id === it.scene_id && o.track !== 'scenes') marks.push(o.start, o.end); });
    let live = null;
    const snap = (v, alt) => {
      if (alt) return { v, at: null };
      let best = null;
      marks.forEach((m) => { if (Math.abs(m - v) * pps <= SNAP_PX && (best == null || Math.abs(m - v) < Math.abs(best - v))) best = m; });
      return best == null ? { v, at: null } : { v: best, at: best };
    };
    const move = (ev) => {
      if (!editable) return;
      const dx = (ev.clientX - x0) / pps;
      if (!live && Math.abs(ev.clientX - x0) < 3) return;
      let ns = s0;
      let ne = e0;
      let at = null;
      if (mode === 'move') {
        ns = clamp(s0 + dx, b0, b1 - len);
        const a = snap(ns, ev.altKey);
        const b = snap(ns + len, ev.altKey);
        if (a.at != null && (b.at == null || Math.abs(a.v - ns) <= Math.abs(b.v - ns - len))) { ns = a.v; at = a.at; } else if (b.at != null) { ns = b.v - len; at = b.at; }
        ns = clamp(ns, b0, b1 - len);
        ne = ns + len;
      } else if (mode === 'start') {
        const a = snap(clamp(s0 + dx, b0, e0 - minLen), ev.altKey);
        ns = clamp(a.v, b0, e0 - minLen); at = a.at;
      } else {
        const b = snap(clamp(e0 + dx, s0 + minLen, b1), ev.altKey);
        ne = clamp(b.v, s0 + minLen, b1); at = b.at;
      }
      live = { id: it.id, start: frame(ns), end: frame(ne), snap: at };
      setDrag(live);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDrag(null);
      if (live && (Math.abs(live.start - s0) > 1e-4 || Math.abs(live.end - e0) > 1e-4)) {
        addEdit({ scene_id: it.scene_id, composition: it.composition, layer_id: it.layer_id, _item: it.id,
          _name: blockLabel(it), type: 'timing', start: live.start, end: live.end });
        setSelId(it.id);
      } else if (!live) {
        const sc = scrollRef.current;
        const at = sc ? (x0 - sc.getBoundingClientRect().left + sc.scrollLeft) / pps : null;
        select(it, at);
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // ── actions on the selected element ──
  const baseOf = (it) => ({ scene_id: it.scene_id, composition: it.composition, layer_id: it.layer_id, _item: it.id, _name: blockLabel(it) });
  const canEdit = sel && sel.editable && EDITABLE_TRACKS.has(sel.track);
  const actions = {
    remove: () => {
      if (!canEdit) return;
      const others = items.filter((x) => x.scene_id === sel.scene_id && x.composition === sel.composition && x.id !== sel.id && x.editable);
      if (!others.length) { toast.error('A scene needs at least one element. Swap it or change it instead.'); return; }
      addEdit({ ...baseOf(sel), type: 'delete' });
      setSelId(null);
    },
    duplicate: () => {
      if (!canEdit) return;
      const len = sel.end - sel.start;
      const [b0, b1] = sel.bounds || [sel.start, sel.end];
      let s = sel.end;
      if (s + Math.min(len, 0.5) > b1) s = b0; // no room after it: copy at the start of the scene
      const e = frame(Math.min(b1, s + len));
      s = frame(Math.max(b0, Math.min(s, e - (data?.limits?.min_len || 0.2))));
      const nid = newId();
      addEdit({ ...baseOf(sel), type: 'duplicate', new_id: nid, start: s, end: e });
      setSelId(`${sel.scene_id}/${sel.composition}/${nid}`);
    },
    split: () => {
      if (!canEdit) return;
      const minLen = data?.limits?.min_len || 0.2;
      if (!(t >= sel.start + minLen && t <= sel.end - minLen)) { toast.error('Put the playhead inside the element (not right at an edge) to split it.'); return; }
      addEdit({ ...baseOf(sel), type: 'split', new_id: newId(), at: frame(t) });
    },
    order: (direction) => { if (canEdit) addEdit({ ...baseOf(sel), type: 'order', direction }); },
  };

  const addFromPalette = (p, extra = {}) => {
    const sc = sceneAt;
    if (!sc) return;
    const minLen = data?.limits?.min_len || 0.2;
    let s = frame(clamp(t, sc.start, sc.end));
    let e = frame(Math.min(sc.end, s + 3));
    if (e - s < Math.max(minLen, 1)) { s = frame(Math.max(sc.start, e - 3)); }
    if (e - s < minLen) { toast.error('This scene is too short to add anything.'); return; }
    const nid = newId();
    const layer = { ...p.layer, ...extra };
    addEdit({ type: 'add', scene_id: sc.id, composition: 0, new_id: nid, start: s, end: e, layer, _palette: p.id });
    setAdding(false);
    setSelId(`${sc.id}/0/${nid}`);
    setTab('content');
    seek(s + Math.min(0.8, (e - s) / 2)); // a frame where the new element is fully on screen
  };

  // keyboard: space play, arrows step, Delete removes, Ctrl+Z / Ctrl+Shift+Z, Ctrl+D copy, S split, Escape, +/- zoom
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target?.isContentEditable) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); return; }
      if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); actions.duplicate(); return; }
      if (e.code === 'Space') { e.preventDefault(); toggle(); } else if (e.key === 'ArrowRight') { seek(t + (e.shiftKey ? 5 : e.altKey ? 1 / FPS : 1)); } else if (e.key === 'ArrowLeft') { seek(t - (e.shiftKey ? 5 : e.altKey ? 1 / FPS : 1)); } else if (e.key === 'Escape') { setSelId(null); setAdding(false); } else if (e.key === 'Delete' || e.key === 'Backspace') { if (sel) { e.preventDefault(); actions.remove(); } } else if (e.key.toLowerCase() === 's' && !mod) { actions.split(); } else if (e.key === '+' || e.key === '=') { setPps((p) => Math.min(300, p * 1.25)); } else if (e.key === '-') { setPps((p) => Math.max(5, p / 1.25)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ctrl/cmd + wheel zooms around the pointer
  const onWheel = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    const sc = scrollRef.current;
    const rect = sc.getBoundingClientRect();
    const at = (e.clientX - rect.left + sc.scrollLeft) / pps;
    const next = Math.max(5, Math.min(300, pps * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
    setPps(next);
    requestAnimationFrame(() => { sc.scrollLeft = at * next - (e.clientX - rect.left); });
  };
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return undefined;
    const h = (e) => { if (e.ctrlKey || e.metaKey) e.preventDefault(); };
    sc.addEventListener('wheel', h, { passive: false });
    return () => sc.removeEventListener('wheel', h);
  }, [data]);

  const submit = () => {
    if (!edits.length) return;
    setSending(true);
    api.post(`/visual-engine/videos/${id}/edits`, { edits: clean(edits) })
      .then(() => {
        try { localStorage.removeItem(draftKey(id)); } catch (_) { /* storage blocked */ }
        setHist({ past: [], now: [], future: [] });
        toast.success(`Rendering ${changedScenes.length} scene${changedScenes.length === 1 ? '' : 's'} again. The new version appears on the project page.`);
        navigate(`/documentaries/${id}`);
      })
      .catch((e) => { toast.error(errorMessage(e)); setSending(false); });
  };

  if (error) {
    return (
      <div className="docs-page"><Header />
        <main className="docs-main docs-main--narrow">
          <Link to={`/documentaries/${id}`} className="docs-back">Back to the project</Link>
          <div className="docs-alert docs-alert--bad">{error}</div>
        </main>
      </div>
    );
  }
  if (!data || !video) return <div className="docs-page"><Header /><main className="docs-main"><div className="docs-empty">Opening the timeline…</div></main></div>;

  const res = video.result || {};
  const width = duration * pps;
  const nEdits = edits.length;
  const showWords = pps >= 45;
  const overlay = showPreview && previewScene && !playing;

  return (
    <div className="docs-page dtl-page">
      <Header />
      <main className="dtl-main">
        <div className="dtl-top">
          <div className="dtl-title">
            <Link to={`/documentaries/${id}`} className="docs-back">{video.title}</Link>
            <h1>Timeline</h1>
            <span className="dtl-version">Version {data.versions?.length || 1}{data.versions?.length > 1 ? `, last change: ${data.versions[data.versions.length - 1].changed}` : ''}</span>
          </div>
          <div className="dtl-actions">
            <button type="button" className="docs-btn docs-btn--ghost" disabled={!hist.past.length} onClick={undo} title="Undo (Ctrl+Z)">Undo</button>
            <button type="button" className="docs-btn docs-btn--ghost" disabled={!hist.future.length} onClick={redo} title="Redo (Ctrl+Shift+Z)">Redo</button>
            {nEdits > 0 && (
              <button type="button" className="docs-btn docs-btn--ghost" onClick={() => change(() => [])}>Discard all</button>
            )}
            <button type="button" className="docs-btn docs-btn--primary" disabled={!nEdits || sending} onClick={submit}>
              {sending ? 'Starting…' : nEdits ? `Render ${changedScenes.length} changed scene${changedScenes.length === 1 ? '' : 's'}` : 'No changes yet'}
            </button>
          </div>
        </div>

        <div className="dtl-stage">
          <div className="dtl-viewer">
            <div className="dtl-player">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video ref={vref} src={mediaUrl(res.video_url)} poster={mediaUrl(res.thumbnail_url)} preload="auto"
                     onClick={toggle} onPlay={() => setPlaying(true)} onPause={() => { setPlaying(false); setT(vref.current?.currentTime || 0); }}
                     onSeeked={() => setT(vref.current?.currentTime || 0)} onEnded={() => setPlaying(false)} />
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
              {overlay && preview.url && <img className="dtl-preview" src={preview.url} alt="This frame with your changes" onClick={toggle} />}
              {safeArea && <div className="dtl-safe" aria-hidden />}
              {overlay && (
                <div className={`dtl-badge ${preview.error ? 'is-bad' : ''}`}>
                  {preview.error ? preview.error : preview.loading ? 'Making a preview of this frame…' : 'Preview with your changes (paused frame)'}
                </div>
              )}
            </div>
            <div className="dtl-viewbar">
              <label className="dtl-switch">
                <input type="checkbox" checked={showPreview} onChange={(e) => setShowPreview(e.target.checked)} />
                <span>Show my changes when paused</span>
              </label>
              <label className="dtl-switch">
                <input type="checkbox" checked={safeArea} onChange={(e) => setSafeArea(e.target.checked)} />
                <span>Safe area</span>
              </label>
              {nEdits > 0 && !previewScene && <span className="dtl-viewbar__hint">Pause on a changed scene to see a preview.</span>}
            </div>
          </div>
          <aside className="dtl-side">
            {adding ? (
              <AddPanel palette={data.palette || []} scene={sceneAt} onAdd={addFromPalette} onClose={() => setAdding(false)} />
            ) : (
              <Inspector videoId={id} data={data} item={sel} scene={sceneAt} edits={edits} addEdit={addEdit}
                         onSeek={seek} t={t} tab={tab} setTab={setTab} actions={actions} />
            )}
            {nEdits > 0 && (
              <div className="dtl-pending">
                <h4>Your changes ({nEdits}) in {changedScenes.length} scene{changedScenes.length === 1 ? '' : 's'}</h4>
                <ul>
                  {edits.map((e) => (
                    <li key={e._k}>
                      <button type="button" className="docs-link" onClick={() => {
                        const it = items.find((x) => x.id === (e._item || `${e.scene_id}/${e.composition}/${e.new_id}`));
                        if (it) select(it); else seek((scenes.find((s) => s.id === e.scene_id)?.start || 0) + 0.01);
                      }}>{describe(e, items)}</button>
                      <button type="button" className="dtl-x" aria-label="Remove this change"
                              onClick={() => change((list) => withoutChange(list, e))}>×</button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>

        <section className="dtl-panel" aria-label="Timeline">
          <div className="dtl-toolbar">
            <button type="button" className="dtl-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <FaPause /> : <FaPlay />}
            </button>
            <span className="dtl-clock">{fmt(t)} <small>/ {fmt(duration)}</small></span>
            <button type="button" className={`docs-btn docs-btn--small ${adding ? 'docs-btn--primary' : ''}`}
                    onClick={() => setAdding((a) => !a)} title="Add text, a graphic or a picture at the playhead">+ Add</button>
            <div className="dtl-tools">
              <button type="button" className="dtl-tool" disabled={!canEdit} onClick={actions.split} title="Split at the playhead (S)">Split</button>
              <button type="button" className="dtl-tool" disabled={!canEdit} onClick={actions.duplicate} title="Copy (Ctrl+D)">Copy</button>
              <button type="button" className="dtl-tool" disabled={!canEdit} onClick={actions.remove} title="Delete (Del)">Delete</button>
            </div>
            <span className="dtl-scene-now">{sceneAt ? (sceneAt.purpose || sceneAt.id) : ''}</span>
            <div className="dtl-zoom">
              <button type="button" className="dtl-zbtn" onClick={() => setPps((p) => Math.max(5, p / 1.4))} aria-label="Zoom out">−</button>
              <input type="range" min={5} max={300} value={pps} onChange={(e) => setPps(Number(e.target.value))} aria-label="Zoom" />
              <button type="button" className="dtl-zbtn" onClick={() => setPps((p) => Math.min(300, p * 1.4))} aria-label="Zoom in">+</button>
              <button type="button" className="docs-btn docs-btn--small" onClick={() => setPps(Math.max(5, ((scrollRef.current?.clientWidth || 1000) - 40) / (duration || 1)))}>Fit</button>
            </div>
          </div>

          <div className="dtl-body">
            <div className="dtl-heads">
              <div className="dtl-head dtl-head--ruler" />
              {TRACKS.map(({ key, name }) => (
                <div key={key} className={`dtl-head dtl-head--${key}`} style={{ height: laneInfo[key].count * LANE_H[key] }}>
                  <span className="dtl-swatch" />{name}
                </div>
              ))}
            </div>
            <div className="dtl-scroll" ref={scrollRef} onWheel={onWheel}>
              <div className="dtl-canvas" style={{ width: width + 40 }}>
                <Ruler duration={duration} pps={pps} onSeek={onScrubStart} />
                {TRACKS.map(({ key }) => (
                  <div key={key} className={`dtl-track dtl-track--${key}`} style={{ height: laneInfo[key].count * LANE_H[key], width }}
                       onPointerDown={(e) => { if (e.target === e.currentTarget) { setSelId(null); onScrubStart(e); } }}>
                    {key === 'voice' ? (
                      showWords ? (data.words || []).map((w, i) => (
                        <button key={i} type="button" className={`dtl-word ${t >= w.t && t < w.e ? 'is-now' : ''}`}
                                style={{ left: w.t * pps, width: Math.max(2, (w.e - w.t) * pps) }}
                                onClick={() => seek(w.t)} title={w.w}>{(w.e - w.t) * pps > 18 ? w.w : ''}</button>
                      )) : scenes.map((s) => (
                        <span key={s.id} className="dtl-narr" style={{ left: s.start * pps, width: (s.end - s.start) * pps }}>
                          {(s.end - s.start) * pps > 60 ? (s.narration || '') : ''}
                        </span>
                      ))
                    ) : (byTrack[key] || []).map((it) => (
                      <Block key={it.id} videoId={id} it={it} pps={pps} lane={laneInfo[key].map.get(it.id) || 0}
                             laneH={LANE_H[key]} selected={sel?.id === it.id}
                             live={drag?.id === it.id ? drag : null}
                             changed={changedItems.has(it.id) || (key === 'scenes' && changedScenes.includes(it.id))}
                             onPointerDown={onBlockPointerDown} />
                    ))}
                  </div>
                ))}
                {sel?.bounds && EDITABLE_TRACKS.has(sel.track) && (
                  <div className="dtl-bounds" style={{ left: sel.bounds[0] * pps, width: (sel.bounds[1] - sel.bounds[0]) * pps }} aria-hidden />
                )}
                {drag?.snap != null && <div className="dtl-snap" style={{ transform: `translateX(${drag.snap * pps}px)` }} aria-hidden />}
                <div className="dtl-playhead" style={{ transform: `translateX(${t * pps}px)` }} onPointerDown={onScrubStart}>
                  <span className="dtl-playhead__knob" />
                </div>
              </div>
            </div>
          </div>
          <p className="dtl-keys">Space plays and pauses. Arrows step one second (Shift: five, Alt: one frame). Drag a block to
            move it, its edges to trim. S splits at the playhead, Ctrl+D copies, Delete removes, Ctrl+Z undoes. Ctrl and the
            scroll wheel zoom.</p>
        </section>
      </main>
    </div>
  );
}
